/**
 * The media library — one page for every image on the platform.
 *
 * Scope is decided entirely by the server (`MediaAssetViewSet.visible_filter`):
 * a broker's staff see their own library plus the shared platform one, a
 * country admin sees everything. Nothing here widens that, and nothing here
 * asks for a broker id — passing one would be the first step towards a
 * cross-tenant leak.
 */

import { useMemo, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CircleStackIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  EmptyState,
  Modal,
  ModalFooter,
  Pagination,
  StatsCard,
  StatsGrid,
} from '@/components/common';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import {
  formatBytes,
  type AssetListParams,
  type MediaAsset,
} from '@/services/assets';
import {
  useAssetConfig,
  useAssetFolders,
  useAssetStats,
  useAssets,
  useDeleteUnusedAssets,
  useUnusedAssets,
} from '@/hooks/useAssets';
import {
  AssetDetailModal,
  AssetFilters,
  AssetGrid,
  EMPTY_FILTERS,
  FolderNav,
  ImageEditorModal,
  MediaDialog,
  UploadDropzone,
  filtersToParams,
  type AssetFilterState,
  type FolderSelection,
} from '@/components/media';

const PAGE_SIZE = 24;
/** `StandardResultsSetPagination.max_page_size` — the most the API will return. */
const SWEEP_BATCH = 100;

export function MediaLibraryPage() {
  const [page, setPage] = useState(1);
  const [folder, setFolder] = useState<FolderSelection>(null);
  const [filters, setFilters] = useState<AssetFilterState>(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [detailAsset, setDetailAsset] = useState<MediaAsset | null>(null);
  const [editingAsset, setEditingAsset] = useState<MediaAsset | null>(null);
  const [sweepOpen, setSweepOpen] = useState(false);

  const { term, debouncedTerm, setTerm } = useDebouncedSearch('');

  const params: AssetListParams = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedTerm || undefined,
      folder: folder === null ? undefined : folder,
      ...filtersToParams(filters),
    }),
    [page, debouncedTerm, folder, filters],
  );

  const assets = useAssets(params);
  const stats = useAssetStats();
  const folders = useAssetFolders();
  const config = useAssetConfig();
  const deleteUnused = useDeleteUnusedAssets();

  // The clean-up sweep works off the server's own "unused" list rather than the
  // rows on screen, so the count in the dialog is the count that gets deleted.
  // `page_size` is the API's ceiling, hence the batch wording.
  const sweep = useUnusedAssets(
    { page_size: SWEEP_BATCH, folder: folder === null ? undefined : folder },
    sweepOpen,
  );
  const sweepBatch = sweep.data?.results || [];
  const sweepTotal = sweep.data?.count || 0;
  const sweepLoading = sweep.isLoading;

  // Memoised because `liveDetailAsset` depends on it — a fresh array literal
  // every render would re-run that lookup (and reset the detail form) on any
  // unrelated state change.
  const results = useMemo(() => assets.data?.results || [], [assets.data]);
  const total = assets.data?.count || 0;

  // The detail modal holds its own copy of the asset so the form does not reset
  // under the user on every background refetch; re-point it at the fresh row
  // when the list comes back with new bytes or metadata.
  const liveDetailAsset = useMemo(() => {
    if (!detailAsset) return null;
    return results.find((asset) => asset.id === detailAsset.id) || detailAsset;
  }, [detailAsset, results]);

  const selectedAssets = results.filter((asset) => selectedIds.includes(asset.id));
  const selectedUnused = selectedAssets.filter((asset) => !asset.in_use);
  const selectedInUse = selectedAssets.length - selectedUnused.length;

  const toggleSelect = (asset: MediaAsset) =>
    setSelectedIds((current) =>
      current.includes(asset.id)
        ? current.filter((id) => id !== asset.id)
        : [...current, asset.id],
    );

  const resetPage = () => setPage(1);

  const byKind = (kind: string) => stats.data?.by_kind.find((row) => row.kind === kind)?.count || 0;

  return (
    <div>
      <PageHeader
        title="Media Library"
        subtitle="Every image on your website, in one place — with alt text, usage and versions."
        breadcrumbs={[{ label: 'Media Library' }]}
        actions={
          <>
            <Button
              variant="outline"
              leftIcon={<TrashIcon className="h-4 w-4" />}
              onClick={() => setSweepOpen(true)}
              disabled={!stats.data?.unused_count}
            >
              Delete unused{stats.data?.unused_count ? ` (${stats.data.unused_count})` : ''}
            </Button>
            <Button leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={() => setUploadOpen(true)}>
              Upload images
            </Button>
          </>
        }
      />

      <StatsGrid columns={4} className="mb-6">
        <StatsCard title="Images" value={stats.data?.count ?? 0} icon={PhotoIcon} loading={stats.isLoading} />
        <StatsCard
          title="Storage used"
          value={formatBytes(stats.data?.total_bytes ?? 0)}
          icon={CircleStackIcon}
          loading={stats.isLoading}
        />
        <StatsCard
          title="Not used anywhere"
          value={stats.data?.unused_count ?? 0}
          subtitle="Safe to delete"
          icon={TrashIcon}
          loading={stats.isLoading}
        />
        <StatsCard
          title="Vectors"
          value={byKind('vector')}
          subtitle={`${byKind('raster')} photos and logos`}
          icon={SparklesIcon}
          loading={stats.isLoading}
        />
      </StatsGrid>

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <Card padding="sm" className="h-fit">
          <FolderNav
            folders={folders.data || []}
            selected={folder}
            onSelect={(next) => {
              setFolder(next);
              resetPage();
            }}
            totalCount={stats.data?.count}
            isLoading={folders.isLoading}
          />
        </Card>

        <div className="space-y-4">
          <AssetFilters
            search={term}
            onSearchChange={(next) => {
              setTerm(next);
              resetPage();
            }}
            filters={filters}
            onFiltersChange={(next) => {
              setFilters(next);
              resetPage();
            }}
            config={config.data}
            showAdvanced={showAdvanced}
            onToggleAdvanced={() => setShowAdvanced((value) => !value)}
          />

          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-900/50 dark:bg-primary-900/20">
              <p className="text-sm text-primary-900 dark:text-primary-200">
                {selectedIds.length} selected
                {selectedInUse > 0 && (
                  <span className="text-primary-700 dark:text-primary-300">
                    {' '}
                    · {selectedInUse} still in use and will be kept
                  </span>
                )}
              </p>
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                  Clear selection
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={deleteUnused.isPending}
                  disabled={!selectedUnused.length}
                  onClick={() =>
                    deleteUnused.mutate(
                      { ids: selectedIds },
                      { onSuccess: () => setSelectedIds([]) },
                    )
                  }
                >
                  Delete {selectedUnused.length} unused
                </Button>
              </div>
            </div>
          )}

          <AssetGrid
            assets={results}
            isLoading={assets.isLoading}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onOpen={setDetailAsset}
            emptyState={
              <Card>
                <EmptyState
                  icon={<PhotoIcon />}
                  title={debouncedTerm || filters.kind ? 'No images match those filters' : 'No images yet'}
                  description={
                    debouncedTerm || filters.kind
                      ? 'Try a broader search, or clear the filters.'
                      : 'Upload your logos, hero images and product artwork once, then reuse them anywhere.'
                  }
                  action={
                    <Button
                      leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
                      onClick={() => setUploadOpen(true)}
                    >
                      Upload images
                    </Button>
                  }
                />
              </Card>
            }
          />

          {total > PAGE_SIZE && (
            <Card padding="none" className="overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={assets.data?.total_pages || 1}
                totalItems={total}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setPage}
              />
            </Card>
          )}
        </div>
      </div>

      {/* --- upload --- */}
      <MediaDialog
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        size="2xl"
        title="Upload images"
        description={
          folder && folder !== 'none'
            ? `They will go into ${folders.data?.find((row) => row.id === folder)?.path || 'the selected folder'}.`
            : 'Drop several at once — each one uploads and reports on its own.'
        }
        closeOnOverlayClick={!uploadBusy}
        footer={
          <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploadBusy}>
            {uploadBusy ? 'Uploading…' : 'Done'}
          </Button>
        }
      >
        <UploadDropzone
          folder={folder === 'none' ? null : folder}
          config={config.data}
          onBusyChange={setUploadBusy}
        />
      </MediaDialog>

      {/* --- detail --- */}
      {liveDetailAsset && (
        <AssetDetailModal
          asset={liveDetailAsset}
          isOpen={Boolean(detailAsset)}
          onClose={() => setDetailAsset(null)}
          folders={folders.data || []}
          config={config.data}
          onEditImage={(asset) => {
            setDetailAsset(null);
            setEditingAsset(asset);
          }}
          onDeleted={(id) => setSelectedIds((current) => current.filter((value) => value !== id))}
        />
      )}

      {/* --- editor --- */}
      {editingAsset && (
        <ImageEditorModal
          asset={editingAsset}
          isOpen={Boolean(editingAsset)}
          onClose={() => setEditingAsset(null)}
          config={config.data}
          onSaved={(asset) => {
            setEditingAsset(null);
            setDetailAsset(asset);
          }}
        />
      )}

      {/* --- sweep --- */}
      <Modal
        isOpen={sweepOpen}
        onClose={() => setSweepOpen(false)}
        title="Delete unused images?"
        size="md"
      >
        <p className="text-sm text-gray-600">
          {sweepLoading
            ? 'Checking which images nothing references…'
            : sweepTotal === 0
              ? 'Every image in this view is used somewhere. Nothing to clean up.'
              : `This removes ${sweepBatch.length} image${sweepBatch.length === 1 ? '' : 's'} that nothing references${
                  folder ? ' in the selected folder' : ''
                }. Anything still in use is skipped by the server, so this cannot break a live page.`}
        </p>
        {sweepTotal > sweepBatch.length && (
          <p className="mt-2 text-sm text-amber-700">
            {sweepTotal} are unused in total — they are removed {SWEEP_BATCH} at a time, so run this again
            to clear the rest.
          </p>
        )}
        <p className="mt-2 text-sm text-gray-500">
          The files are removed from storage and cannot be restored.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setSweepOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={deleteUnused.isPending}
            disabled={sweepLoading || !sweepBatch.length}
            onClick={() =>
              deleteUnused.mutate(
                // The endpoint refuses a bare "sweep the whole library" call, so
                // it is always given explicit ids — read from /assets/unused/,
                // which is the server's own answer to "what is safe to delete".
                { ids: sweepBatch.map((asset) => asset.id) },
                { onSuccess: () => setSweepOpen(false) },
              )
            }
          >
            Delete {sweepBatch.length || ''} unused
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default MediaLibraryPage;
